import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { endOfDay, startOfDay } from "date-fns";
import { useSelectedHotelId } from "@/utils/hotelContext";
import { getHttpStatus } from "@/utils/httpErrors";
import { useDebounce } from "@/hooks/useDebounce";
import { useDateRange } from "@/hooks/useDateRange";
import { PageState } from "@/components/common/PageState";
import { Button } from "@/components/ui/button";
import { MessageDeliveryFilters } from "@/types/message-delivery";
import { useMessageDeliveries } from "./hooks/message-deliveries.hooks";
import {
  ALL_FILTER,
  ChannelFilter,
  DeliveryFilters,
  DirectionFilter,
  StatusFilter,
} from "./components/DeliveryFilters";
import { DeliveriesTable } from "./components/DeliveriesTable";

const DeliveriesHeader: React.FC = () => {
  const { t } = useTranslation();
  return (
    <header className="flex flex-col gap-2">
      <h1 className="text-2xl font-semibold text-black/96">
        {t("communicationChannels.deliveries.page.title")}
      </h1>
      <p className="max-w-2xl text-sm text-black/65">
        {t("communicationChannels.deliveries.page.description")}
      </p>
    </header>
  );
};

export const CommunicationDeliveriesPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const hotelId = useSelectedHotelId();
  const [searchParams, setSearchParams] = useSearchParams();

  const bookingId = searchParams.get("bookingId") ?? undefined;
  const [direction, setDirection] = React.useState<DirectionFilter>(ALL_FILTER);
  const [status, setStatus] = React.useState<StatusFilter>(ALL_FILTER);
  const [channel, setChannel] = React.useState<ChannelFilter>(ALL_FILTER);
  const [recipient, setRecipient] = React.useState("");
  const { dateRange, setDateRange, resetDateRange } = useDateRange();

  const debouncedRecipient = useDebounce(recipient, 400);

  const filters = React.useMemo<MessageDeliveryFilters>(
    () => ({
      bookingId,
      channel: channel === ALL_FILTER ? undefined : channel,
      direction: direction === ALL_FILTER ? undefined : direction,
      status: status === ALL_FILTER ? undefined : status,
      from: dateRange.from
        ? startOfDay(dateRange.from).toISOString()
        : undefined,
      to: dateRange.to ? endOfDay(dateRange.to).toISOString() : undefined,
      recipientAddressFragment: debouncedRecipient.trim() || undefined,
    }),
    [
      bookingId,
      channel,
      direction,
      status,
      dateRange.from,
      dateRange.to,
      debouncedRecipient,
    ],
  );

  const {
    rows,
    isLoading,
    isError,
    error,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useMessageDeliveries(hotelId, filters);

  const handleClearBooking = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("bookingId");
    setSearchParams(next);
  };

  const handleClearFilters = () => {
    setDirection(ALL_FILTER);
    setStatus(ALL_FILTER);
    setChannel(ALL_FILTER);
    setRecipient("");
    resetDateRange();
    handleClearBooking();
  };

  if (!hotelId) {
    return (
      <PageState
        text={t("communicationChannels.deliveries.page.noHotelSelected")}
      />
    );
  }

  if (isError && getHttpStatus(error) === 503) {
    return (
      <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6 p-6 md:p-8">
        <DeliveriesHeader />
        <div className="border-custom-destructive-dark/40 bg-custom-destructive-dark/5 text-custom-destructive-dark rounded-md border p-4 text-sm">
          {t("communicationChannels.killSwitch")}
        </div>
      </div>
    );
  }

  const showLoadError = isError && getHttpStatus(error) !== 503;

  return (
    <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6 p-6 md:p-8">
      <DeliveriesHeader />

      <DeliveryFilters
        direction={direction}
        onDirectionChange={setDirection}
        status={status}
        onStatusChange={setStatus}
        channel={channel}
        onChannelChange={setChannel}
        recipient={recipient}
        onRecipientChange={setRecipient}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        bookingId={bookingId}
        onClearBooking={handleClearBooking}
        onRefresh={() => refetch()}
        onClear={handleClearFilters}
        isFetching={isFetching}
      />

      <DeliveriesTable
        rows={rows}
        isLoading={isLoading}
        isError={showLoadError}
      />

      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="new_outline"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {t("communicationChannels.deliveries.loadMore")}
          </Button>
        </div>
      )}
    </div>
  );
};
